import { Paper, Container, Typography, Stack, Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FoodsIcon } from "@foodpilot/foods";
import { useState, useEffect } from "react";
import {
    useApplicationParameters,
    appParamCacheKey,
    ApplicationParameterKeys,
} from "src/api/client-api/appParams/useApplicationParameters";
import { useUpsertAppParam } from "src/api/client-api/appParams";
import { useSnackbar } from "src/utils/useSnackbar";
import { useQueryClient } from "react-query";
import { IntegrationSection } from "./Integrations/IntegrationSection";
import { useNavigateSettingsParent } from "./useNavigateSettingsParent";

export default function Integrations() {
    const { t } = useTranslation();
    const navigateSettingsParent = useNavigateSettingsParent();
    const { applicationParameters } = useApplicationParameters();
    const upsertAppParam = useUpsertAppParam({ forceReload: false });
    const snackbar = useSnackbar();
    const queryClient = useQueryClient();

    // Local state for Freshdesk Widget ID
    const [freshdeskWidgetId, setFreshdeskWidgetId] = useState("");
    const [freshdeskOriginal, setFreshdeskOriginal] = useState("");

    // Local state for Amplitude API Key
    const [amplitudeApiKey, setAmplitudeApiKey] = useState("");
    const [amplitudeOriginal, setAmplitudeOriginal] = useState("");

    // Local state for GTM IDs
    const [gtmIdGlobal, setGtmIdGlobal] = useState("");
    const [gtmIdGlobalOriginal, setGtmIdGlobalOriginal] = useState("");
    const [gtmIdClient, setGtmIdClient] = useState("");
    const [gtmIdClientOriginal, setGtmIdClientOriginal] = useState("");

    const [isSaving, setIsSaving] = useState(false);

    // Initialize form with current values
    useEffect(() => {
        if (applicationParameters) {
            const freshdeskValue = applicationParameters.freshdeskWidgetId || "";
            setFreshdeskWidgetId(freshdeskValue);
            setFreshdeskOriginal(freshdeskValue);

            const amplitudeValue = applicationParameters.amplitudeApiKey || "";
            setAmplitudeApiKey(amplitudeValue);
            setAmplitudeOriginal(amplitudeValue);

            const gtmGlobalValue = applicationParameters.gtmIdGlobal || "";
            setGtmIdGlobal(gtmGlobalValue);
            setGtmIdGlobalOriginal(gtmGlobalValue);

            const gtmClientValue = applicationParameters.gtmIdClient || "";
            setGtmIdClient(gtmClientValue);
            setGtmIdClientOriginal(gtmClientValue);
        }
    }, [applicationParameters]);

    const handleGoBack = () => {
        navigateSettingsParent();
    };

    // Helper function to save a single parameter
    const saveParameter = async (key: ApplicationParameterKeys, value: string, originalValue: string) => {
        const wasValueOriginallySet = applicationParameters?.[key] !== undefined;

        await upsertAppParam.mutateAsync({
            isNew: !wasValueOriginallySet,
            key,
            value: value.trim(),
        });
    };

    // Freshdesk handlers
    const handleFreshdeskReset = () => {
        setFreshdeskWidgetId(freshdeskOriginal);
    };

    const handleFreshdeskValidate = async () => {
        if (freshdeskWidgetId.trim() === freshdeskOriginal) {
            snackbar.setSnackbarProps({
                type: "info",
                message: t("integrations.noChanges", "Aucune modification à sauvegarder"),
            });
            return;
        }

        setIsSaving(true);
        try {
            await saveParameter("freshdeskWidgetId", freshdeskWidgetId, freshdeskOriginal);
            setFreshdeskOriginal(freshdeskWidgetId.trim());
            snackbar.setSnackbarProps({
                type: "success",
                message: t("integrations.saveSuccess", "Configuration sauvegardée avec succès"),
            });
            queryClient.refetchQueries([appParamCacheKey]);
        } catch (error) {
            snackbar.setSnackbarProps({
                type: "error",
                message: t("integrations.saveError", "Erreur lors de la sauvegarde de la configuration"),
            });
        } finally {
            setIsSaving(false);
        }
    };

    // Amplitude handlers
    const handleAmplitudeReset = () => {
        setAmplitudeApiKey(amplitudeOriginal);
    };

    const handleAmplitudeValidate = async () => {
        if (amplitudeApiKey.trim() === amplitudeOriginal) {
            snackbar.setSnackbarProps({
                type: "info",
                message: t("integrations.noChanges", "Aucune modification à sauvegarder"),
            });
            return;
        }

        setIsSaving(true);
        try {
            await saveParameter("amplitudeApiKey", amplitudeApiKey, amplitudeOriginal);
            setAmplitudeOriginal(amplitudeApiKey.trim());
            snackbar.setSnackbarProps({
                type: "success",
                message: t("integrations.amplitude.saveSuccess", "Amplitude configuré avec succès"),
            });
            queryClient.refetchQueries([appParamCacheKey]);
        } catch (error) {
            snackbar.setSnackbarProps({
                type: "error",
                message: t("integrations.saveError", "Erreur lors de la sauvegarde de la configuration"),
            });
        } finally {
            setIsSaving(false);
        }
    };

    // GTM handlers
    const handleGTMReset = () => {
        setGtmIdGlobal(gtmIdGlobalOriginal);
        setGtmIdClient(gtmIdClientOriginal);
    };

    const handleGTMValidate = async () => {
        const globalChanged = gtmIdGlobal.trim() !== gtmIdGlobalOriginal;
        const clientChanged = gtmIdClient.trim() !== gtmIdClientOriginal;

        if (!globalChanged && !clientChanged) {
            snackbar.setSnackbarProps({
                type: "info",
                message: t("integrations.noChanges", "Aucune modification à sauvegarder"),
            });
            return;
        }

        setIsSaving(true);
        try {
            if (globalChanged) {
                await saveParameter("gtmIdGlobal", gtmIdGlobal, gtmIdGlobalOriginal);
                setGtmIdGlobalOriginal(gtmIdGlobal.trim());
            }
            if (clientChanged) {
                await saveParameter("gtmIdClient", gtmIdClient, gtmIdClientOriginal);
                setGtmIdClientOriginal(gtmIdClient.trim());
            }

            snackbar.setSnackbarProps({
                type: "success",
                message: t("integrations.gtm.saveSuccess", "Google Tag Manager configuré avec succès"),
            });
            queryClient.refetchQueries([appParamCacheKey]);
        } catch (error) {
            snackbar.setSnackbarProps({
                type: "error",
                message: t("integrations.saveError", "Erreur lors de la sauvegarde de la configuration"),
            });
        } finally {
            setIsSaving(false);
        }
    };

    // Check for unsaved changes
    const hasFreshdeskChanges = freshdeskWidgetId.trim() !== freshdeskOriginal;
    const hasAmplitudeChanges = amplitudeApiKey.trim() !== amplitudeOriginal;
    const hasGTMChanges = gtmIdGlobal.trim() !== gtmIdGlobalOriginal || gtmIdClient.trim() !== gtmIdClientOriginal;

    return (
        <Container
            component="main"
            maxWidth={false}
            sx={{
                m: "0",
                p: "48px !important",
                flexGrow: 1,
                overflow: "auto",
            }}
        >
            {/* Main Container */}
            <Paper
                elevation={0}
                sx={{
                    border: "1px solid #E7E6E4",
                    borderRadius: "16px",
                    overflow: "hidden",
                    backgroundColor: "white",
                }}
            >
                {/* Header with back button */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "28px 32px",
                        borderBottom: "1px solid #E7E6E4",
                        backgroundColor: "white",
                    }}
                >
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <Box
                            onClick={handleGoBack}
                            sx={{
                                width: "40px",
                                height: "40px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                "&:hover": {
                                    backgroundColor: "#F9F9F9",
                                    borderRadius: "4px",
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    "& svg": {
                                        strokeWidth: "2px",
                                        stroke: "currentColor",
                                    },
                                }}
                            >
                                <FoodsIcon icon="arrowLeftShort" size={6} />
                            </Box>
                        </Box>
                        <Typography variant="number-inter" sx={{ lineHeight: "1.6em", color: "#393A36" }}>
                            {t("integrations.title", "Integrations")}
                        </Typography>
                    </Stack>
                </Box>

                {/* Content */}
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "32px",
                        padding: "32px",
                    }}
                >
                    {/* Freshdesk Integration */}
                    <IntegrationSection
                        title={t("integrations.freshdesk.title", "Freshdesk")}
                        description={t(
                            "integrations.freshdesk.description",
                            "Configure the Freshdesk widget for client support",
                        )}
                        fields={[
                            {
                                label: t("integrations.freshdesk.widgetId", "Widget ID"),
                                value: freshdeskWidgetId,
                                onChange: setFreshdeskWidgetId,
                                placeholder: t("integrations.freshdesk.placeholder", "Ex: 123456789"),
                            },
                        ]}
                        onReset={handleFreshdeskReset}
                        onValidate={handleFreshdeskValidate}
                        hasChanges={hasFreshdeskChanges}
                        isValid={freshdeskWidgetId.trim().length > 0}
                        isSaving={isSaving}
                    />

                    {/* Amplitude Integration */}
                    <IntegrationSection
                        title={t("integrations.amplitude.title", "Amplitude Analytics")}
                        description={t(
                            "integrations.amplitude.description",
                            "follow user events and analyze behavior with Amplitude",
                        )}
                        fields={[
                            {
                                label: t("integrations.amplitude.apiKey", "API Key"),
                                value: amplitudeApiKey,
                                onChange: setAmplitudeApiKey,
                                placeholder: t(
                                    "integrations.amplitude.placeholder",
                                    "Ex: 68e9112e7b02a6cfca00f1628b8a03ba",
                                ),
                                helperText: t(
                                    "integrations.amplitude.helper",
                                    "Find your API key in the Amplitude project settings",
                                ),
                            },
                        ]}
                        onReset={handleAmplitudeReset}
                        onValidate={handleAmplitudeValidate}
                        hasChanges={hasAmplitudeChanges}
                        isValid={amplitudeApiKey.trim().length > 0}
                        isSaving={isSaving}
                    />

                    {/* GTM Integration */}
                    <IntegrationSection
                        title={t("integrations.gtm.title", "Google Tag Manager")}
                        description={t(
                            "integrations.gtm.description",
                            "configure the GTM containers for multi-level tracking (global + client)",
                        )}
                        fields={[
                            {
                                label: t("integrations.gtm.globalId", "Container Global ID (optional)"),
                                value: gtmIdGlobal,
                                onChange: setGtmIdGlobal,
                                placeholder: t("integrations.gtm.globalPlaceholder", "Ex: GTM-XXXXXX"),
                                helperText: t("integrations.gtm.globalHelper", "Container for the entire platform"),
                            },
                            {
                                label: t("integrations.gtm.clientId", "Container Client ID (optional)"),
                                value: gtmIdClient,
                                onChange: setGtmIdClient,
                                placeholder: t("integrations.gtm.clientPlaceholder", "Ex: GTM-YYYYYY"),
                                helperText: t("integrations.gtm.clientHelper", "Container specific to this client"),
                            },
                        ]}
                        onReset={handleGTMReset}
                        onValidate={handleGTMValidate}
                        hasChanges={hasGTMChanges}
                        isValid={gtmIdGlobal.trim().length > 0 || gtmIdClient.trim().length > 0}
                        isSaving={isSaving}
                    />
                </Box>
            </Paper>
        </Container>
    );
}
